import React, { useState, useEffect } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Alert } from 'reactstrap'
import { useMutation } from '@apollo/client'
import { CREATE_TOPIC, UPDATE_TOPIC } from './Mutations'

export default function CreateEditTopicModal({ isOpen, toggle, topic, onSuccess }) {
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [errors, setErrors] = useState([])

    const isEdit = !!topic

    useEffect(() => {
        setName(topic?.name || '')
        setDescription(topic?.description || '')
        setErrors([])
    }, [topic, isOpen])

    const [createTopic, { loading: creating }] = useMutation(CREATE_TOPIC, {
        onCompleted: (data) => {
            if (data.messagingCreateTopic.ok) {
                const createdName = data.messagingCreateTopic.topic?.name || name.trim()
                toggle()
                if (onSuccess) onSuccess(`Topic "${createdName}" created successfully.`)
            } else {
                setErrors(data.messagingCreateTopic.errors || ['Failed to create topic'])
            }
        },
        onError: (error) => {
            setErrors([error.message])
        }
    })

    const [updateTopic, { loading: updating }] = useMutation(UPDATE_TOPIC, {
        onCompleted: (data) => {
            if (data.messagingUpdateTopic.ok) {
                const updatedName = data.messagingUpdateTopic.topic?.name || name.trim()
                toggle()
                if (onSuccess) onSuccess(`Topic "${updatedName}" updated successfully.`)
            } else {
                setErrors(data.messagingUpdateTopic.errors || ['Failed to update topic'])
            }
        },
        onError: (error) => {
            setErrors([error.message])
        }
    })

    const handleSubmit = (e) => {
        e.preventDefault()
        setErrors([])

        if (!name.trim()) {
            setErrors(['Name is required'])
            return
        }

        if (isEdit) {
            updateTopic({
                variables: {
                    id: topic.id,
                    name: name.trim(),
                    description: description.trim()
                }
            })
        } else {
            createTopic({
                variables: {
                    name: name.trim(),
                    description: description.trim()
                }
            })
        }
    }

    const loading = creating || updating

    return (
        <Modal isOpen={isOpen}>
            <ModalHeader>
                {isEdit ? 'Edit Topic' : 'Create New Topic'}
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody>
                    {errors.length > 0 && (
                        <Alert color="danger">
                            {errors.map((error, idx) => (
                                <div key={idx}>{error}</div>
                            ))}
                        </Alert>
                    )}
                    <FormGroup>
                        <Label for="topicName">Name *</Label>
                        <Input
                            type="text"
                            id="topicName"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Enter topic name"
                            disabled={loading}
                            required
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label for="topicDescription">Description</Label>
                        <Input
                            type="textarea"
                            id="topicDescription"
                            rows={4}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Describe what messages belong under this topic"
                            disabled={loading}
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle} disabled={loading}>
                        Cancel
                    </Button>
                    <Button color="primary" type="submit" disabled={loading}>
                        {loading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    )
}
